import { create } from 'zustand';
import {
  listProcedures,
  listProceduresForDept,
  setProcedureDepartments as apiSetProcedureDepartments,
  createProcedure as apiCreateProcedure,
  updateProcedure as apiUpdateProcedure,
  deactivateProcedure as apiDeactivateProcedure,
} from '../services/api/procedureApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Procedure Catalog Store (Zustand)
 *
 * Filter semantics:
 *   - ``procedures`` always holds the FULL catalog for the clinic (the
 *     backend ``category`` filter is NOT used so that per-category pill-
 *     tab counts stay accurate when the user switches categories).
 *   - ``selectedCategory`` + ``searchQuery`` are client-side filters
 *     applied by the UI (see visible memo in the page component).
 *   - ``includeInactive`` DOES go to the backend because deactivated
 *     rows are excluded server-side by default.
 *
 * A4 / S3 picker slice (SEPARATE — RC-1):
 *   - ``pickerProcedures`` is the SERVER-scoped add-procedure picker list for
 *     ONE visit's department (dept-tagged ∪ org-wide/untagged, filtered
 *     server-side). It is a DISTINCT slice from the admin ``procedures`` catalog
 *     so the picker never mutates/over-broadens the admin list and the admin
 *     list never leaks an out-of-scope row into a picker. Do NOT merge them.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useProcedureStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  procedures: [],
  selectedCategory: 'all',   // 'all' | 'dental' | 'gp' | 'derm' | 'cardio' | 'ortho' | 'ent'
  includeInactive: false,
  searchQuery: '',
  isLoading: false,
  error: null,

  // ── A4 picker slice (separate from admin `procedures`) ─────────────
  pickerProcedures: [],
  pickerDeptId: undefined,   // undefined = never fetched; null = "no filter / full catalog"
  isPickerLoading: false,
  // single-flight guard — dedup concurrent/repeat picker fetches so a consumer
  // effect re-firing can't storm requests (memory: runtime-safety).
  _pickerInFlight: null,     // { deptId, promise }

  // ── Fetch (always the full catalog — category is client-side) ──────
  fetchProcedures: async () => {
    const { includeInactive } = get();
    set({ isLoading: true, error: null });

    const result = await listProcedures({
      include_inactive: includeInactive,
    });

    if (result.success) {
      set({ procedures: result.data.procedures || [], isLoading: false });
    } else {
      set({ error: result.error, isLoading: false });
    }
    return result;
  },

  // ── A4 / S3 — scoped picker fetch → writes ONLY `pickerProcedures` ──
  // `deptId` is the visit's department anchor (null/falsy → full catalog, param
  // omitted server-side). Idempotent + single-flight: a repeat call for the
  // dept we already hold is a no-op; a concurrent call for the same dept shares
  // the in-flight promise. The admin `procedures` slice is NEVER touched here.
  fetchProceduresForDept: async (deptId) => {
    const anchor = deptId || null;
    const { pickerDeptId, _pickerInFlight } = get();

    // Already hold this dept's list and nothing in flight → idempotent no-op.
    if (pickerDeptId === anchor && !_pickerInFlight) {
      return { success: true, data: { procedures: get().pickerProcedures } };
    }
    // Same dept already loading → share the in-flight promise (single-flight).
    if (_pickerInFlight && _pickerInFlight.deptId === anchor) {
      return _pickerInFlight.promise;
    }

    set({ isPickerLoading: true });
    const promise = (async () => {
      const result = await listProceduresForDept({ department_id: anchor });
      // Latest-wins: only apply if this is still the current in-flight request
      // (a newer dept anchor may have superseded it).
      if (get()._pickerInFlight?.promise !== promise) return result;
      if (result.success) {
        set({
          pickerProcedures: result.data.procedures || [],
          pickerDeptId: anchor,
          isPickerLoading: false,
          _pickerInFlight: null,
        });
      } else {
        set({ isPickerLoading: false, _pickerInFlight: null });
      }
      return result;
    })();
    set({ _pickerInFlight: { deptId: anchor, promise } });
    return promise;
  },

  // ── A4 / S2 — admin dept tagging (C2). Patches the admin `procedures`
  // row in place with the server's post-flip owner_scope + department_ids so
  // the catalog reflects the change immediately (single source). ──────────
  setProcedureDepartments: async (category, procedure_id, department_ids) => {
    const result = await apiSetProcedureDepartments(category, procedure_id, department_ids);
    if (result.success && result.data) {
      set((s) => ({
        procedures: s.procedures.map((p) =>
          p.procedure_id === procedure_id && p.category === category
            ? {
                ...p,
                owner_scope: result.data.owner_scope,
                department_ids: result.data.department_ids || [],
              }
            : p
        ),
      }));
    }
    return result;
  },

  // ── Create ─────────────────────────────────────────────────────────
  createProcedure: async (body) => {
    const result = await apiCreateProcedure(body);
    if (result.success) {
      // Re-fetch so filters/categories stay consistent (cheaper than
      // surgically inserting and risking a filter mismatch).
      await get().fetchProcedures();
    }
    return result;
  },

  // ── Update (partial) ───────────────────────────────────────────────
  updateProcedure: async (category, procedure_id, body) => {
    const result = await apiUpdateProcedure(category, procedure_id, body);
    if (result.success) {
      // Patch in place to preserve current filters/scroll.
      set((s) => ({
        procedures: s.procedures.map((p) =>
          p.procedure_id === procedure_id && p.category === category
            ? { ...p, ...(result.data || body) }
            : p
        ),
      }));
    }
    return result;
  },

  // ── Deactivate (soft-delete) ───────────────────────────────────────
  deactivateProcedure: async (category, procedure_id) => {
    const result = await apiDeactivateProcedure(category, procedure_id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        procedures: includeInactive
          // keep it, just mark inactive
          ? s.procedures.map((p) =>
              p.procedure_id === procedure_id && p.category === category
                ? { ...p, active: false }
                : p
            )
          // hide it
          : s.procedures.filter(
              (p) => !(p.procedure_id === procedure_id && p.category === category)
            ),
      }));
    }
    return result;
  },

  // ── Filter setters ─────────────────────────────────────────────────
  // selectedCategory + searchQuery are client-side — no refetch needed.
  // includeInactive IS a backend filter so it refetches.
  setCategory: (category) => set({ selectedCategory: category }),

  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    get().fetchProcedures();
  },

  setSearch: (query) => set({ searchQuery: query || '' }),

  clearError: () => set({ error: null }),
}));

export default useProcedureStore;
